import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useErrorTracking } from "@/hooks/useErrorTracking";
import AppLayout from "./components/AppLayout";
import { GlobalRoofingCoPilot } from "@/components/GlobalRoofingCoPilot";
import { AppTelemetry } from "@/components/AppTelemetry";
import Dashboard from "./pages/Dashboard";
import Auth from "./pages/Auth";
import FieldModeCapture from "./pages/FieldModeCapture";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import CreateJob from "./pages/CreateJob";
import CreateJobAi from "./pages/CreateJobAi";
import Leads from "./pages/Leads";
import LeadDetail from "./pages/LeadDetail";
import CreateLead from "./pages/CreateLead";
import SalesCommandCenter from "./pages/SalesCommandCenter";
import Measurements from "./pages/Measurements";
import PhotosAI from "./pages/PhotosAI";
import FormsTemplatesHub from "./pages/FormsTemplatesHub";
import Templates from "./pages/Templates";
import CrewManagement from "./pages/CrewManagement";
import DispatchBoard from "./pages/DispatchBoard";
import MaterialOrders from "./pages/MaterialOrders";
import PermitsHub from "./pages/PermitsHub";
import WorkflowAutomation from "./pages/WorkflowAutomation";
import TemplateBuilder from "./pages/TemplateBuilder";
import PublicProposal from "./pages/PublicProposal";
import ProposalReview from "./pages/ProposalReview";
import PublicDigitalProposal from "./pages/PublicDigitalProposal";
import DigitalProposalReview from "./pages/DigitalProposalReview";
import ClaimReader from "./pages/ClaimReader";
import Commercial from "./pages/Commercial";
import Settings from "./pages/Settings";
import Help from "./pages/Help";
import HelpArticle from "./pages/HelpArticle";
import PricingSettings from "./pages/PricingSettings";
import AgentControl from "./pages/AgentControl";
import DroneIntegration from "./pages/DroneIntegration";
import SystemMonitor from "./pages/SystemMonitor";
import Portal from "./pages/Portal";
import Catalog from "./pages/Catalog";
import AgentDashboard from "./pages/AgentDashboard";
import PlatformAdmin from "./pages/PlatformAdmin";
import EmailWorkspace from "./pages/EmailWorkspace";
import Reports from "./pages/Reports";
import NotFound from "./pages/NotFound";

import BridgetteWorkspace from "./pages/BridgetteWorkspace";
import RBServiceAgreement from "./pages/RBServiceAgreement";
import RetailEntryPage from "./pages/retail/RetailEntryPage";
import RetailPreparePage from "./pages/retail/RetailPreparePage";
import RetailProposalPage from "./pages/retail/RetailProposalPage";

const queryClient = new QueryClient();

import UniversalDocImport from "./pages/UniversalDocImport";
import ResetPassword from "./pages/ResetPassword";
import Calendar from "./pages/Calendar";
import Unsubscribe from "./pages/Unsubscribe";
import TeamChat from "./pages/TeamChat";
import Campaigns from "./pages/Campaigns";
import Pipeline from "./pages/Pipeline";
import DemoDataSetup from "./pages/DemoDataSetup";
import MobileInspectionFlow from "./pages/MobileInspectionFlow";
import QuickEstimate from "./pages/QuickEstimate";
import Install from "./pages/Install";
import StormMap from "./pages/StormMap";
import CanvassingHub from "./pages/CanvassingHub";
import SalesLeaderboard from "./pages/SalesLeaderboard";
import Financials from "./pages/Financials";
import ReceiptsInbox from "./pages/ReceiptsInbox";
import { Navigate } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";

const App = () => {
  // Initialize error tracking with user context
  useErrorTracking();

  return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthScopedQueryReset />
          <ErrorBoundary>
            <AppTelemetry />
            <GlobalRoofingCoPilot />
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/demo-setup" element={<DemoDataSetup />} />
              <Route path="/install" element={<Install />} />
              
              {/* Public routes */}
              <Route path="/portal/:id" element={<Portal />} />
              <Route path="/proposal/:shareId" element={<PublicProposal />} />
              <Route path="/digital-proposal/:shareId" element={<PublicDigitalProposal />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              
              {/* Protected routes with AppLayout */}
              <Route path="/" element={<AppLayout><Dashboard /></AppLayout>} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/jobs" element={<AppLayout><Jobs /></AppLayout>} />
              <Route path="/jobs/new" element={<AppLayout><CreateJob /></AppLayout>} />
              <Route path="/jobs/new/ai" element={<CreateJobAi />} />
              <Route path="/jobs/:id" element={<AppLayout><JobDetail /></AppLayout>} />
              <Route path="/receipts/inbox" element={<AppLayout><ReceiptsInbox /></AppLayout>} />
              <Route path="/sales-hub" element={<SalesCommandCenter />} />
              <Route path="/leaderboard" element={<SalesLeaderboard />} />
              <Route path="/leads" element={<SalesCommandCenter />} />
              <Route path="/leads/new" element={<AppLayout><CreateLead /></AppLayout>} />
              <Route path="/leads/:id" element={<AppLayout><LeadDetail /></AppLayout>} />
              <Route path="/email" element={<AppLayout><EmailWorkspace /></AppLayout>} />
              <Route path="/measurements" element={<AppLayout><Measurements /></AppLayout>} />
              <Route path="/photos-ai" element={<AppLayout><PhotosAI /></AppLayout>} />
              <Route path="/templates" element={<AppLayout><Templates /></AppLayout>} />
              <Route path="/forms" element={<AppLayout><FormsTemplatesHub /></AppLayout>} />
              <Route path="/service-agreement/:jobId" element={<AppLayout><RBServiceAgreement /></AppLayout>} />
              <Route path="/service-agreement/:jobId/:submissionId" element={<AppLayout><RBServiceAgreement /></AppLayout>} />
              <Route path="/crews" element={<AppLayout><CrewManagement /></AppLayout>} />
              <Route path="/dispatch" element={<AppLayout><DispatchBoard /></AppLayout>} />
              <Route path="/material-orders" element={<AppLayout><MaterialOrders /></AppLayout>} />
              <Route path="/permits" element={<PermitsHub />} />
              <Route path="/workflow-automation" element={<AppLayout><WorkflowAutomation /></AppLayout>} />
              <Route path="/reports" element={<AppLayout><Reports /></AppLayout>} />
              {/* Redirects from old routes to new Reports hub */}
              <Route path="/analytics" element={<Navigate to="/reports?tab=operations" replace />} />
              <Route path="/financials" element={<AppLayout><Financials /></AppLayout>} />
              <Route path="/finances" element={<Navigate to="/financials" replace />} />
              <Route path="/data-health" element={<Navigate to="/reports?tab=data-health" replace />} />
              <Route path="/ai-monitoring" element={<Navigate to="/reports?tab=ai-insights" replace />} />
              <Route path="/templates/builder/:id" element={<TemplateBuilder />} />
              <Route path="/proposals/review/:id" element={<AppLayout><ProposalReview /></AppLayout>} />
              <Route path="/digital-proposals/review/:id" element={<AppLayout><DigitalProposalReview /></AppLayout>} />
              <Route path="/import-template" element={<AppLayout><UniversalDocImport /></AppLayout>} />
              <Route path="/claim-reader" element={<AppLayout><ClaimReader /></AppLayout>} />
              <Route path="/bridgette/workspace" element={<AppLayout><BridgetteWorkspace /></AppLayout>} />
              <Route path="/commercial" element={<AppLayout><Commercial /></AppLayout>} />
              <Route path="/help" element={<AppLayout><Help /></AppLayout>} />
              <Route path="/help/:slug" element={<AppLayout><HelpArticle /></AppLayout>} />
              <Route path="/field-capture" element={<FieldModeCapture />} />
              <Route path="/mobile-inspection" element={<MobileInspectionFlow />} />
              <Route path="/quick-estimate" element={<QuickEstimate />} />
              <Route path="/settings" element={<AppLayout><Settings /></AppLayout>} />
              <Route path="/settings/pricing" element={<AppLayout><PricingSettings /></AppLayout>} />
              <Route path="/agent-control" element={<AgentControl />} />
              <Route path="/drone" element={<AppLayout><DroneIntegration /></AppLayout>} />
              <Route path="/storm-watch" element={<AppLayout><StormMap /></AppLayout>} />
              <Route path="/canvassing" element={<CanvassingHub />} />
              <Route path="/system-monitor" element={<SystemMonitor />} />
              <Route path="/retail" element={<RetailEntryPage />} />
              <Route path="/retail/prepare" element={<RetailPreparePage />} />
              <Route path="/retail/proposal/:id" element={<RetailProposalPage />} />
              {/* Legacy routes - redirect to unified system monitor */}
              <Route path="/queue-monitor" element={<SystemMonitor />} />
              <Route path="/error-monitor" element={<SystemMonitor />} />
              <Route path="/agent" element={<AgentDashboard />} />
              <Route path="catalog" element={<AppLayout><Catalog /></AppLayout>} />
              
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/team-chat" element={<AppLayout><TeamChat /></AppLayout>} />
              <Route path="/platform-admin" element={<PlatformAdmin />} />
              <Route path="/admin" element={<Navigate to="/platform-admin" replace />} />
              <Route path="/campaigns" element={<AppLayout><Campaigns /></AppLayout>} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  );
};

function AuthScopedQueryReset() {
  const { user, orgId } = useAuthContext();

  useEffect(() => {
    queryClient.clear();
  }, [user?.id, orgId]);

  return null;
}

export default App;
